import React from 'react'
import {
  Datagrid,
  DateField,
  Pagination,
  ReferenceManyField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  useRefresh,
  useShowContext,
} from 'react-admin'
import FileActionButtons from './FileActionButtons'
import StateField from './StateField'

const getImportStatus = (record: any) => {
  const importStatus = record?.status?.import_log_status
  return typeof importStatus === 'string' ? importStatus.toUpperCase() : ''
}

const isActiveImportStatus = (status: string) => {
  return (
    status === 'QUEUED' ||
    status === 'STARTED' ||
    status === 'RUNNING' ||
    status.startsWith('PREPARING') ||
    status.startsWith('PROCESSING')
  )
}

const isTerminalImportState = (record: any) => {
  if (!record) return false

  const status = getImportStatus(record)
  if (isActiveImportStatus(status)) {
    return false
  }
  if (status === 'COMPLETED' || status === 'FAILED' || status === 'ERROR') {
    return true
  }

  if (record.processed_state === 1 || record.processed_state === 2) {
    return true
  }

  return false
}

const ImportLogAutoRefresh = () => {
  const refresh = useRefresh()
  const { record } = useShowContext()
  const [startedImportId, setStartedImportId] = React.useState<number | null>(
    null
  )
  const [hasSeenActiveState, setHasSeenActiveState] = React.useState(false)

  React.useEffect(() => {
    const handleImportStarted = (event: Event) => {
      const { detail } = event as CustomEvent
      if (!detail?.id) return
      setStartedImportId(Number(detail.id))
      setHasSeenActiveState(false)
      refresh()
    }

    window.addEventListener('import-log-started', handleImportStarted)
    return () => {
      window.removeEventListener('import-log-started', handleImportStarted)
    }
  }, [refresh])

  React.useEffect(() => {
    if (!record?.id || startedImportId !== record.id) return

    if (
      isActiveImportStatus(getImportStatus(record)) ||
      record.processed_state === 0
    ) {
      setHasSeenActiveState(true)
      return
    }

    if (hasSeenActiveState && isTerminalImportState(record)) {
      setStartedImportId(null)
      setHasSeenActiveState(false)
    }
  }, [hasSeenActiveState, record, startedImportId])

  React.useEffect(() => {
    let pollInterval: number | null = null

    const forcePolling = Boolean(record?.id && startedImportId === record.id)
    if (record?.id && (forcePolling || !isTerminalImportState(record))) {
      pollInterval = window.setInterval(() => {
        refresh()
      }, 2000)
    }

    return () => {
      if (pollInterval !== null) {
        window.clearInterval(pollInterval)
      }
    }
  }, [refresh, record, startedImportId])

  return null
}

const ImportLogShow = () => {
  return (
    <Show>
      <ImportLogAutoRefresh />
      <TabbedShowLayout>
        <Tab label="summary">
          <TextField source="filename" emptyText="n/a" />
          <TextField source="user.name" emptyText="n/a" />
          <TextField source="organisation.name" emptyText="n/a" />
          <DateField source="created" label="Date uploaded" />
          <TextField
            source="status.import_log_status"
            label="Import Log Status"
          />
          <TextField
            source="status.import_log_message"
            label="Import Log Message"
          />
          <StateField label="Processed State" />
          <TextField source="status_message" label="Status Message" />
          <TextField source="status.import_log_error" label="Import Error" />
          <TextField
            source="status.import_log_total"
            label="Import Log Total"
          />
          <TextField
            source="status.import_log_current"
            label="Import Log Current"
          />
          <TextField
            source="status.organisation_status"
            label="Organisation Map Status"
          />
          <TextField
            source="status.organisation_total"
            label="Organisation Map Total"
          />
          <TextField
            source="status.organisation_current"
            label="Organisation Map Current"
          />
          <FileActionButtons />
        </Tab>
        <Tab label="vendors">
          <ReferenceManyField
            reference="vendors"
            target="import_log_id"
            source="id"
            pagination={<Pagination />}
          >
            <Datagrid bulkActionButtons={false}>
              <TextField source="name" label="Vendor" />
              <TextField source="territory.name" label="Territory" />
            </Datagrid>
          </ReferenceManyField>
        </Tab>
        <Tab label="transactions">
          <ReferenceManyField
            reference="transactions"
            target="import_log_id"
            source="id"
            pagination={<Pagination />}
          >
            <Datagrid bulkActionButtons={false}>
              <TextField source="invoice_vendor_name" label="Vendor" />
              <TextField source="amount" label="Amount" />
            </Datagrid>
          </ReferenceManyField>
        </Tab>
      </TabbedShowLayout>
    </Show>
  )
}

export default ImportLogShow
